import { useCallback, useState, useEffect, useRef } from 'react'

export type EmojiObject = {
  id: string
  keywords: string[]
  name: string
  native: string
  shortcodes: string
  unified: string
}

export interface EmojiMartPicker {
  (props: {
    previewEmoji: string
    perLine: number
    data: any
    theme: 'light' | 'dark'
    onEmojiSelect: (emoji: EmojiObject) => void
  }): JSX.Element
}

export interface EmojiMartModules {
  data: any
  Picker: EmojiMartPicker
  init: (config: { data: any }) => void
}

export type EmojiPickerLoadingState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; modules: EmojiMartModules }
  | { status: 'error'; error: string }

// Singleton cache for emoji-mart modules
let moduleCache: EmojiMartModules | null = null
let loadingPromise: Promise<EmojiMartModules> | null = null

async function loadEmojiMartModules(): Promise<EmojiMartModules> {
  // Return cached modules if already loaded
  if (moduleCache) {
    return moduleCache
  }

  // Return existing loading promise if already in progress
  if (loadingPromise) {
    return loadingPromise
  }

  // Start loading modules
  loadingPromise = Promise.all([
    import('@emoji-mart/data'),
    import('@emoji-mart/react'),
    import('emoji-mart'),
  ]).then(([dataModule, pickerModule, initModule]) => {
    moduleCache = {
      data: dataModule.default,
      Picker: pickerModule.default,
      init: initModule.init,
    }
    loadingPromise = null // Reset for future loads if needed
    return moduleCache
  })

  return loadingPromise
}

type UseEmojiMartLoaderResult = {
  loadingState: EmojiPickerLoadingState
  loadModules: () => Promise<void>
}

/**
 * Custom hook to manage emoji picker loading with proper cleanup and cancellation.
 *
 * Features:
 * - Singleton pattern for module caching across component instances
 * - AbortController for proper async operation cancellation
 * - Automatic cleanup on component unmount
 * - Functional state updates to avoid unnecessary re-renders
 *
 * @returns Object containing loading state and function to load emoji-mart modules
 *
 * @example
 * ```tsx
 * const { loadingState, loadModules } = useEmojiMartLoader()
 *
 * useEffect(() => {
 *   loadModules()
 * }, [loadModules])
 *
 * if (loadingState.status === 'ready') {
 *   return <EmojiPicker modules={loadingState.modules} />
 * }
 * ```
 */
export const useEmojiMartLoader = (): UseEmojiMartLoaderResult => {
  const [loadingState, setLoadingState] = useState<EmojiPickerLoadingState>({
    status: 'idle',
  })
  // Web standard for cancelling operations
  const abortControllerRef = useRef<AbortController | null>(null)

  const loadModules = useCallback(async (): Promise<void> => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController()
    const { signal } = abortControllerRef.current

    setLoadingState((prevState) =>
      prevState.status === 'idle' ? { status: 'loading' } : prevState,
    )

    try {
      const modules = await loadEmojiMartModules()

      // Check if operation was cancelled
      if (!signal.aborted) {
        setLoadingState({ status: 'ready', modules })
      }
    } catch (error) {
      if (!signal.aborted) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        // eslint-disable-next-line no-console
        console.error('Failed to load emoji-mart modules:', error)
        setLoadingState({ status: 'error', error: errorMessage })
      }
    }
  }, [])

  // Cleanup
  useEffect(() => {
    return (): void => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return { loadingState, loadModules }
}
